import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Modal,
  FlatList,
  Alert,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { launchCamera, launchImageLibrary, ImagePickerResponse, Asset } from 'react-native-image-picker';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { homeInventoryInfoService } from '../../../../services/homeInventoryInfo';
import { vaultService } from '../../../../services/vaultService';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('HomeInventoryInput');

interface PhotoItem {
  key?: string;      // S3 key for saved photos
  url: string;       // Display URL (either base64 data URI or signed S3 URL)
  base64?: string;   // Base64 data for pending uploads
  mimeType?: string; // MIME type for upload
  isPending?: boolean; // True if not yet uploaded to S3
}

const CATEGORIES = [
  'Electronics',
  'Furniture',
  'Jewelry',
  'Artwork',
  'Appliances',
  'Tools',
  'Clothing',
  'Books',
  'Sports Equipment',
  'Musical Instruments',
  'Collectibles',
  'Other',
];

const CONDITIONS = [
  'Excellent',
  'Good',
  'Fair',
  'Poor',
];

export const HomeInventoryInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, { homeInventoryId?: string; isEdit?: boolean }>, string>>();
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showConditionModal, setShowConditionModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showAddAnotherDone, setShowAddAnotherDone] = useState(false);
  const [itemPhotos, setItemPhotos] = useState<PhotoItem[]>([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [createdHomeInventoryId, setCreatedHomeInventoryId] = useState<string | null>(null);
  const homeInventoryId = (route.params as any)?.homeInventoryId as string | undefined;
  const isEdit = !!(route.params as any)?.isEdit;
  const [savedHomeInventoryId, setSavedHomeInventoryId] = useState<string | null>(null);

  useEffect(() => {
    if (isEdit && homeInventoryId) {
      (async () => {
        try {
          // Load existing home inventory record
          const data = await homeInventoryInfoService.getAllHomeInventoryInfo();
          const record = data.find((item: any) => item.id === homeInventoryId);
          if (record) {
            logger.debug('[Edit Debug] Loading home inventory for edit:', record);
            setFormData({
              itemName: record.itemName || '',
              category: record.category || '',
              brand: record.brand || '',
              model: record.model || '',
              serialNumber: '', // This will be loaded from vault
              purchaseDate: record.purchaseDate || '',
              purchasePrice: record.purchasePrice ? `$${record.purchasePrice.toLocaleString()}` : '',
              currentValue: record.currentValue ? `$${record.currentValue.toLocaleString()}` : '',
              condition: record.condition || '',
              locationInHome: record.locationInHome || '',
              warrantyExpiration: record.warrantyExpiration || '',
              notes: record.notes || '',
            });
            // Try to load serial number from vault
            try {
              const serialData = await vaultService.getHomeInventoryData(homeInventoryId);
              if (serialData && serialData.serialNumber) {
                setFormData(prev => ({ ...prev, serialNumber: serialData.serialNumber }));
              }
            } catch {}
            // Load existing photos
            try {
              const photos = await homeInventoryInfoService.getPhotos(homeInventoryId);
              if (photos && photos.length > 0) {
                setItemPhotos(photos.map(p => ({
                  key: p.key,
                  url: p.url,
                  isPending: false,
                })));
                logger.debug('[Edit Debug] Loaded photos:', photos.length);
              }
            } catch (e) {
              logger.debug('Failed to load photos for edit', e);
            }
          }
        } catch (e) {
          logger.debug('Failed to load home inventory for edit', e);
        }
      })();
    }
  }, [isEdit, homeInventoryId]);
  
  const [formData, setFormData] = useState({
    itemName: '',
    category: '',
    brand: '',
    model: '',
    serialNumber: '',
    purchaseDate: '',
    purchasePrice: '',
    currentValue: '',
    condition: '',
    locationInHome: '',
    warrantyExpiration: '',
    notes: '',
  });

  // Format currency input with $x,xxx,xxx format
  const formatCurrency = (value: string, field: 'purchasePrice' | 'currentValue') => {
    // Remove all non-numeric characters except decimal
    const numbers = value.replace(/[^\d.]/g, '');
    
    // Split on decimal
    const parts = numbers.split('.');
    
    // Don't allow multiple decimals
    if (parts.length > 2) {
      return field === 'purchasePrice' ? formData.purchasePrice : formData.currentValue;
    }
    
    // Format the integer part with commas
    if (parts[0]) {
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    
    // Limit decimal places to 2
    if (parts[1] && parts[1].length > 2) {
      parts[1] = parts[1].substring(0, 2);
    }
    
    // Combine and add dollar sign
    const formatted = parts.join('.');
    return formatted ? `$${formatted}` : '';
  };

  // Format date input as MM/DD/YYYY
  const formatDate = (text: string, field: 'purchaseDate' | 'warrantyExpiration') => {
    const cleaned = text.replace(/\D/g, '');
    let formatted = cleaned;
    
    if (cleaned.length >= 2) {
      formatted = cleaned.slice(0, 2) + '/' + cleaned.slice(2);
    }
    if (cleaned.length >= 4) {
      formatted = cleaned.slice(0, 2) + '/' + cleaned.slice(2, 4) + '/' + cleaned.slice(4, 8);
    }
    
    return formatted;
  };

  const handleTakePhoto = () => {
    Alert.alert(
      'Add Photo',
      'Choose an option',
      [
        {
          text: 'Take Photo',
          onPress: () => openCamera(),
        },
        {
          text: 'Choose from Gallery',
          onPress: () => openGallery(),
        },
        {
          text: 'Cancel',
          style: 'cancel',
        },
      ],
      { cancelable: true }
    );
  };

  const openCamera = async () => {
    try {
      const result: ImagePickerResponse = await launchCamera({
        mediaType: 'photo',
        includeBase64: true,
        quality: 0.8,
        maxWidth: 1200,
        maxHeight: 1200,
      });

      handleImagePickerResponse(result);
    } catch (error) {
      logger.error('Error launching camera:', error);
      Alert.alert('Error', 'Failed to open camera');
    }
  };

  const openGallery = async () => {
    try {
      const result: ImagePickerResponse = await launchImageLibrary({
        mediaType: 'photo',
        includeBase64: true,
        quality: 0.8,
        maxWidth: 1200,
        maxHeight: 1200,
        selectionLimit: 5, // Allow selecting multiple photos
      });

      handleImagePickerResponse(result);
    } catch (error) {
      logger.error('Error launching gallery:', error);
      Alert.alert('Error', 'Failed to open gallery');
    }
  };

  const handleImagePickerResponse = (response: ImagePickerResponse) => {
    if (response.didCancel) {
      logger.debug('User cancelled image picker');
      return;
    }

    if (response.errorCode) {
      logger.error('Image picker error:', response.errorCode, response.errorMessage);
      Alert.alert('Error', response.errorMessage || 'Failed to select image');
      return;
    }

    if (response.assets && response.assets.length > 0) {
      const newPhotos: PhotoItem[] = response.assets
        .filter((asset: Asset) => asset.base64 && asset.uri)
        .map((asset: Asset) => ({
          url: asset.uri!,
          base64: asset.base64,
          mimeType: asset.type || 'image/jpeg',
          isPending: true,
        }));

      setItemPhotos(prev => [...prev, ...newPhotos]);
      logger.debug('Added photos:', newPhotos.length);
    }
  };

  const removePhoto = async (index: number) => {
    const photo = itemPhotos[index];

    // If this is a saved photo (has a key), delete from S3
    if (photo.key && !photo.isPending && (homeInventoryId || savedHomeInventoryId)) {
      try {
        const id = homeInventoryId || savedHomeInventoryId;
        await homeInventoryInfoService.deletePhoto(id!, photo.key);
        logger.debug('Deleted photo from S3:', photo.key);
      } catch (error) {
        logger.error('Error deleting photo from S3:', error);
        Alert.alert('Error', 'Failed to delete photo. Please try again.');
        return;
      }
    }

    const newPhotos = [...itemPhotos];
    newPhotos.splice(index, 1);
    setItemPhotos(newPhotos);
  };

  // Upload pending photos to S3
  const uploadPendingPhotos = async (recordId: string): Promise<void> => {
    const pendingPhotos = itemPhotos.filter(p => p.isPending && p.base64);

    if (pendingPhotos.length === 0) {
      logger.debug('No pending photos to upload');
      return;
    }

    logger.debug(`Uploading ${pendingPhotos.length} pending photos...`);
    setUploadingPhoto(true);

    try {
      for (const photo of pendingPhotos) {
        const result = await homeInventoryInfoService.uploadPhoto(
          recordId,
          photo.base64!,
          photo.mimeType
        );
        logger.debug('Uploaded photo:', result.key);

        // Update the photo in state with the S3 key and URL
        setItemPhotos(prev => prev.map(p =>
          p === photo
            ? { ...p, key: result.key, url: result.url, isPending: false, base64: undefined }
            : p
        ));
      }
      logger.debug('All photos uploaded successfully');
    } catch (error) {
      logger.error('Error uploading photos:', error);
      Alert.alert('Warning', 'Some photos may not have been uploaded. You can add them again later.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      
      // Prepare data for API call (without serialNumber - stored in vault)
      const homeInventoryData = {
        itemName: formData.itemName || undefined,
        category: formData.category || undefined,
        brand: formData.brand || undefined,
        model: formData.model || undefined,
        purchaseDate: formData.purchaseDate || undefined,
        purchasePrice: formData.purchasePrice ? parseFloat(formData.purchasePrice.replace(/[$,]/g, '')) : undefined,
        currentValue: formData.currentValue ? parseFloat(formData.currentValue.replace(/[$,]/g, '')) : undefined,
        condition: formData.condition || undefined,
        locationInHome: formData.locationInHome || undefined,
        warrantyExpiration: formData.warrantyExpiration || undefined,
        notes: formData.notes || undefined,
      };
      
      logger.debug('Saving home inventory data:', homeInventoryData);
      
      // Create or update home inventory record
      let savedId = homeInventoryId as string | undefined;
      if (isEdit && homeInventoryId) {
        const updated = await homeInventoryInfoService.updateHomeInventoryInfo(homeInventoryId, homeInventoryData);
        // Handle wrapped response format { success, data, message }
        const updatedData = updated?.data || updated;
        savedId = updatedData.id || homeInventoryId;
        logger.debug('Home inventory updated successfully:', updatedData);
      } else {
        const saved = await homeInventoryInfoService.createHomeInventoryInfo(homeInventoryData);
        // Handle wrapped response format { success, data, message }
        const createdData = saved?.data || saved;
        savedId = createdData.id;
        logger.debug('Home inventory created successfully:', createdData);
      }
      
      // Store the saved ID for navigation
      setSavedHomeInventoryId(savedId);
      setCreatedHomeInventoryId(savedId);
      
      // Store serial number in vault if provided
      if (savedId && formData.serialNumber) {
        try {
          await vaultService.storeHomeInventoryData(savedId, {
            serialNumber: formData.serialNumber
          });
          logger.debug('Serial number stored in vault successfully');
        } catch (e) {
          logger.error('Vault write failed for home inventory serial number:', e);
        }
      }

      // Upload any pending photos to S3
      if (savedId) {
        await uploadPendingPhotos(savedId);
      }

      // Show Add Another / Done buttons after successful save
      setShowAddAnotherDone(true);
    } catch (error: any) {
      logger.error('Error saving home inventory info:', error);
      Alert.alert('Error', error.message || 'Failed to save home inventory information. Please try again.');
      setSubmitting(false);
    }
  };

  const handleAddAnother = () => {
    // Clear form and hide buttons
    setFormData({
      itemName: '',
      category: '',
      brand: '',
      model: '',
      serialNumber: '',
      purchaseDate: '',
      purchasePrice: '',
      currentValue: '',
      condition: '',
      locationInHome: '',
      warrantyExpiration: '',
      notes: '',
    });
    setItemPhotos([]);
    setShowAddAnotherDone(false);
    setSubmitting(false);
    setCreatedHomeInventoryId(null);
  };

  const handleDone = () => {
    setSubmitting(false);
    const idToUse = savedHomeInventoryId || createdHomeInventoryId || homeInventoryId;
    if (idToUse) {
      (navigation as any).navigate('HomeInventoryLockUnlock', { homeInventoryId: idToUse });
    } else {
      Alert.alert('Error', 'Missing home inventory ID');
    }
  };

  const handleCategorySelect = (category: string) => {
    setFormData({ ...formData, category });
    setShowCategoryModal(false);
  };

  const handleConditionSelect = (condition: string) => {
    setFormData({ ...formData, condition });
    setShowConditionModal(false);
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>{isEdit ? 'Edit' : 'Add'} Valuables & Home Inventory</Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Item Name *</Text>
            <TextInput
              style={styles.input}
              value={formData.itemName}
              onChangeText={(text) => setFormData({ ...formData, itemName: text })}
              placeholder="Ex: 65 inch Samsung TV"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Category</Text>
            <TouchableOpacity
              style={styles.input}
              onPress={() => setShowCategoryModal(true)}
            >
              <Text style={formData.category ? styles.inputText : styles.placeholderText}>
                {formData.category || 'Select Category'}
              </Text>
              <Image
                source={require('../../../../assets/icons/down.png')}
                style={styles.dropdownIcon}
              />
            </TouchableOpacity>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Brand</Text>
            <TextInput
              style={styles.input}
              value={formData.brand}
              onChangeText={(text) => setFormData({ ...formData, brand: text })}
              placeholder="Ex: Samsung"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Model</Text>
            <TextInput
              style={styles.input}
              value={formData.model}
              onChangeText={(text) => setFormData({ ...formData, model: text })}
              placeholder="Ex: QN65Q90TAFXZA"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Serial Number</Text>
            <TextInput
              style={styles.input}
              value={formData.serialNumber}
              onChangeText={(text) => setFormData({ ...formData, serialNumber: text })}
              placeholder="Ex: SN123456789"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Purchase Date</Text>
            <TextInput
              style={styles.input}
              value={formData.purchaseDate}
              onChangeText={(text) => setFormData({ ...formData, purchaseDate: formatDate(text, 'purchaseDate') })}
              placeholder="MM/DD/YYYY"
              placeholderTextColor="#A9A9A9"
              keyboardType="number-pad"
              maxLength={10}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Purchase Price</Text>
            <TextInput
              style={styles.input}
              value={formData.purchasePrice}
              onChangeText={(text) => setFormData({ ...formData, purchasePrice: formatCurrency(text, 'purchasePrice') })}
              placeholder="$1,500.00"
              placeholderTextColor="#A9A9A9"
              keyboardType="decimal-pad"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Current Estimated Value</Text>
            <TextInput
              style={styles.input}
              value={formData.currentValue}
              onChangeText={(text) => setFormData({ ...formData, currentValue: formatCurrency(text, 'currentValue') })}
              placeholder="$1,200.00"
              placeholderTextColor="#A9A9A9"
              keyboardType="decimal-pad"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Condition</Text>
            <TouchableOpacity
              style={styles.input}
              onPress={() => setShowConditionModal(true)}
            >
              <Text style={formData.condition ? styles.inputText : styles.placeholderText}>
                {formData.condition || 'Select Condition'}
              </Text>
              <Image
                source={require('../../../../assets/icons/down.png')}
                style={styles.dropdownIcon}
              />
            </TouchableOpacity>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Location in Home</Text>
            <TextInput
              style={styles.input}
              value={formData.locationInHome}
              onChangeText={(text) => setFormData({ ...formData, locationInHome: text })}
              placeholder="Ex: Living Room Entertainment Center"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Warranty Expiration</Text>
            <TextInput
              style={styles.input}
              value={formData.warrantyExpiration}
              onChangeText={(text) => setFormData({ ...formData, warrantyExpiration: formatDate(text, 'warrantyExpiration') })}
              placeholder="MM/DD/YYYY"
              placeholderTextColor="#A9A9A9"
              keyboardType="number-pad"
              maxLength={10}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Photos of Item</Text>
            <TouchableOpacity
              style={styles.photoButton}
              onPress={handleTakePhoto}
            >
              <Image
                source={require('../../../../assets/icons/camera.png')}
                style={styles.cameraIcon}
              />
              <Text style={styles.photoButtonText}>Take or Select Photo</Text>
            </TouchableOpacity>
            
            {uploadingPhoto && (
              <View style={styles.uploadingContainer}>
                <ActivityIndicator size="small" color="#29BFC0" />
                <Text style={styles.uploadingText}>Uploading photos...</Text>
              </View>
            )}

            {itemPhotos.length > 0 && (
              <View style={styles.photoPreviewContainer}>
                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                  {itemPhotos.map((photo, index) => (
                    <View key={photo.key || index} style={styles.photoWrapper}>
                      <Image source={{ uri: photo.url }} style={styles.photoPreview} />
                      {photo.isPending && (
                        <View style={styles.pendingBadge}>
                          <Text style={styles.pendingBadgeText}>New</Text>
                        </View>
                      )}
                      <TouchableOpacity
                        style={styles.removePhotoButton}
                        onPress={() => removePhoto(index)}
                      >
                        <Text style={styles.removePhotoText}>×</Text>
                      </TouchableOpacity>
                    </View>
                  ))}
                </ScrollView>
              </View>
            )}
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Notes</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.notes}
              onChangeText={(text) => setFormData({ ...formData, notes: text })}
              placeholder="Ex: Gift from grandmother, has sentimental value"
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={4}
            />
          </View>

          {showAddAnotherDone ? (
            <View style={styles.addAnotherDoneContainer}>
              <TouchableOpacity
                style={[styles.submitButton, styles.addAnotherButton]}
                onPress={handleAddAnother}
              >
                <Text style={styles.submitButtonText}>Add Another</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={[styles.submitButton, styles.doneButton]}
                onPress={handleDone}
              >
                <Text style={styles.submitButtonText}>Done</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.submitButtonContainer}>
              {submitting ? (
                <ActivityIndicator size="large" color="#29BFC0" />
              ) : (
                <TouchableOpacity
                  style={styles.submitButton}
                  onPress={handleSubmit}
                >
                  <Text style={styles.submitButtonText}>{isEdit ? 'Update' : 'Submit'}</Text>
                </TouchableOpacity>
              )}
            </View>
          )}

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      {/* Category Modal */}
      <Modal
        visible={showCategoryModal}
        transparent={true}
        animationType="slide"
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowCategoryModal(false)}
        >
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Category</Text>
              <TouchableOpacity
                style={styles.modalCloseButton}
                onPress={() => setShowCategoryModal(false)}
              >
                <Text style={styles.modalCloseText}>×</Text>
              </TouchableOpacity>
            </View>
            <FlatList
              data={CATEGORIES}
              keyExtractor={(item) => item}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={styles.modalItem}
                  onPress={() => handleCategorySelect(item)}
                >
                  <Text style={styles.modalItemText}>{item}</Text>
                </TouchableOpacity>
              )}
            />
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Condition Modal */}
      <Modal
        visible={showConditionModal}
        transparent={true}
        animationType="slide"
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowConditionModal(false)}
        >
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Condition</Text>
              <TouchableOpacity
                style={styles.modalCloseButton}
                onPress={() => setShowConditionModal(false)}
              >
                <Text style={styles.modalCloseText}>×</Text>
              </TouchableOpacity>
            </View>
            <FlatList
              data={CONDITIONS}
              keyExtractor={(item) => item}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={styles.modalItem}
                  onPress={() => handleConditionSelect(item)}
                >
                  <Text style={styles.modalItemText}>{item}</Text>
                </TouchableOpacity>
              )}
            />
          </View>
        </TouchableOpacity>
      </Modal>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(120),
    height: s(120),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    paddingHorizontal: ms(10),
    marginBottom: ms(8),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  inputText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  placeholderText: {
    fontSize: ms(16, 0.3),
    color: '#A9A9A9',
  },
  textArea: {
    height: s(100),
    textAlignVertical: 'top',
  },
  dropdownIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#6B7280',
  },
  photoButton: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
    borderStyle: 'dashed',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: ms(10),
  },
  cameraIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#29BFC0',
  },
  photoButtonText: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    fontWeight: '600',
  },
  photoPreviewContainer: {
    marginTop: ms(10),
  },
  photoWrapper: {
    marginRight: ms(10),
    position: 'relative',
  },
  photoPreview: {
    width: s(100),
    height: s(100),
    borderRadius: ms(10),
  },
  removePhotoButton: {
    position: 'absolute',
    top: ms(-5),
    right: ms(-5),
    backgroundColor: '#FF6B6B',
    borderRadius: ms(12),
    width: s(24),
    height: s(24),
    alignItems: 'center',
    justifyContent: 'center',
  },
  removePhotoText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
  },
  uploadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: ms(10),
    gap: ms(8),
  },
  uploadingText: {
    fontSize: ms(14, 0.3),
    color: '#29BFC0',
  },
  pendingBadge: {
    position: 'absolute',
    bottom: ms(5),
    left: ms(5),
    backgroundColor: '#FF9F5A',
    borderRadius: ms(8),
    paddingHorizontal: ms(6),
    paddingVertical: ms(2),
  },
  pendingBadgeText: {
    color: '#FFFFFF',
    fontSize: ms(10, 0.3),
    fontWeight: '600',
  },
  submitButtonContainer: {
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(60),
    alignItems: 'center',
    marginBottom: ms(120),
    minWidth: s(200),
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  addAnotherDoneContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
    gap: ms(20),
  },
  addAnotherButton: {
    backgroundColor: '#FF9F5A',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  doneButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  bottomPadding: {
    height: s(120),
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    maxHeight: '50%',
    width: '100%',
    paddingBottom: ms(20),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: ms(16),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  modalTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  modalCloseButton: {
    padding: ms(8),
  },
  modalCloseText: {
    fontSize: ms(24, 0.3),
    color: '#1C2C4F',
  },
  modalItem: {
    paddingVertical: ms(12),
    paddingHorizontal: ms(16),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  modalItemText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
});